// docs/117 TT6 — the Thing Below's arms on the board. When an arm comes up (the first board that
// shows it, and every later surfacing; core/creatures/tentacles.ts says when) the water darkens and
// churns in a ring round its square, bubbles boil up, the board rumbles and shakes, then the arm
// bursts up out of the water in a column of spray, rising and uncurling from the waterline, the
// arms of one surfacing one after another. In the desert the sand cracks, sprays up with it and
// pours off it. An arm that goes under sinks from where it stood with a last swirl. Standing, each
// arm sways a little about its base. Reduce motion keeps the sounds and draws the arms standing;
// the effect intensity thins the spray and bubbles.
import Phaser from 'phaser';
import { TEX, ensureSpellTextures } from '../spells/textures';
import { playSfx } from '../../core/audio';
import { EMERGE, armGround, riseFrame, surfacing, swayOf, type ArmSeen } from '../../core/creatures/tentacles';
import type { CreatureGround } from '../creatureArt';
import type { UnitSprite } from './UnitSprite';
import { oneShot } from './oneShots';

type P = { x: number; y: number };

export interface TentacleFxHost {
  scene: Phaser.Scene;
  /** The board's effects layer (spray, droplets, falling sand). */
  add(o: Phaser.GameObjects.GameObject): void;
  /** Under the figures (the dark ring, cracks, churn, ripples). */
  addGround(o: Phaser.GameObjects.GameObject): void;
  cell: number;
  /** The board camera shakes (the scene skips it under reduce motion). */
  shake(ms: number, intensity: number): void;
  /** Stereo placement of a unit's sounds. */
  pan?(unitId: number): number | undefined;
}

export interface TentacleQuality { density: number; still: boolean }

const LOOK: Record<CreatureGround, { dark: number; foam: number[]; spray: number[]; crack: number }> = {
  water: { dark: 0x061420, foam: [0xe8f4ff, 0xbfe0f0, 0x9cc8e0], spray: [0xffffff, 0xd8eeff, 0xa8d4ee, 0x7fb6d8], crack: 0x0b2236 },
  sand: { dark: 0x4a3218, foam: [0xe8cf98, 0xd6b578, 0xc49a58], spray: [0xf0dca8, 0xdcc08a, 0xc8a066, 0xa98048], crack: 0x2e1e0e },
};

/** What a review asks of it (`__fkTentacles`). */
export interface TentacleInfo { up: number[]; risen: Array<{ unitId: number; ground: CreatureGround; delayMs: number }>; sunk: number[]; swaying: number }

export class TentacleFx {
  private live = new Set<Phaser.GameObjects.GameObject>();
  private up = new Set<number>();
  private last = new Map<number, { at: P; key: string; frame: string; flip: boolean; ground: CreatureGround }>();
  private sways = new Map<number, Phaser.Tweens.Tween>();
  private encounter: unknown;
  readonly risen: TentacleInfo['risen'] = [];
  readonly sunk: number[] = [];

  constructor(private readonly host: TentacleFxHost, private quality: TentacleQuality) { ensureSpellTextures(host.scene); }

  setQuality(q: TentacleQuality): void { this.quality = q; }

  /** A new fight: nothing of the Thing Below is up yet. */
  begin(encounter: unknown): void {
    if (encounter === this.encounter) return;
    this.encounter = encounter;
    this.up.clear();
    this.last.clear();
  }

  /** A skipped playback or a fresh board: every puff, ring and drop goes (the arms stay). */
  clear(): void {
    for (const o of this.live) o.destroy();
    this.live.clear();
  }

  info(): TentacleInfo {
    return { up: [...this.up].sort((a, b) => a - b), risen: [...this.risen], sunk: [...this.sunk], swaying: this.sways.size };
  }

  /**
   * The board drew `units` with `sprites`: arms not up before rise (staggered), arms gone under
   * sink from where they last stood, standing arms sway.
   */
  board(units: readonly ArmSeen[], sprites: ReadonlyMap<number, UnitSprite>): void {
    const s = surfacing(this.up, units);
    for (const id of s.sink) {
      const was = this.last.get(id);
      if (was) this.sink(id, was);
    }
    this.up = s.up;
    for (const id of [...this.last.keys()]) if (!s.up.has(id)) this.last.delete(id);
    const rising = new Map(s.rise.map((r) => [r.unitId, r.delayMs]));
    for (const id of s.up) {
      const sp = sprites.get(id);
      if (!sp) continue;
      const delay = rising.get(id);
      if (delay !== undefined) this.emerge(sp, delay, s.rise[0]?.unitId === id);
      else this.sway(sp);
    }
  }

  /** One arm came into the board mid-playback (it surfaced, or came into sight): it rises now unless it is up already. */
  arrive(sp: UnitSprite): void {
    const id = sp.unit.unitId;
    if (!armGround(sp.unit)) return;
    if (this.up.has(id)) { this.sway(sp); return; }
    this.up.add(id);
    this.emerge(sp, 0, true);
  }

  /** Plays one arm's emergence (`lead`: the first of its surfacing, which rumbles and shakes the board). */
  emerge(sp: UnitSprite, delayMs: number, lead: boolean): void {
    const ground = armGround(sp.unit) ?? 'water';
    const id = sp.unit.unitId;
    this.remember(sp, ground);
    this.risen.push({ unitId: id, ground, delayMs });
    if (this.risen.length > 64) this.risen.shift();
    const scene = this.host.scene;
    const pan = this.host.pan?.(id);
    const p = pan !== undefined ? { pan } : {};
    const later = lead ? 1 : 0.7;
    const sounds = () => {
      if (lead) playSfx('tentacle.rumble', { gain: 0.9, ...p });
      scene.time.delayedCall(EMERGE.churnMs, () => playSfx(ground === 'sand' ? 'tentacle.sand' : 'tentacle.burst', { gain: 0.8 * later, ...p }));
    };
    if (this.quality.still) {
      // reduce motion: the arm simply stands there, and is heard coming up
      scene.time.delayedCall(delayMs, sounds);
      this.sway(sp);
      return;
    }
    const bodies = sp.bodies;
    // each body where it stands (the outline copies sit a pixel off the body)
    const homes = bodies.map((b): [number, number] => [b.x, b.y]);
    const n = sp.sprite.scaleY || 1;
    const rows = sp.sprite.frame.height;
    const cols = sp.sprite.frame.width;
    // under the water until its moment
    const paint = (k: number) => {
      const f = riseFrame(k, rows);
      bodies.forEach((b, i) => {
        const [hx, hy] = homes[i] ?? [0, 0];
        if (f.shown <= 0) { b.setVisible(false); return; }
        b.setVisible(true);
        b.setCrop(0, 0, cols, Math.min(rows, f.shown));
        b.setPosition(hx, hy + f.sunk * n);
        b.setScale(n * f.squash, n * f.stretch);
      });
    };
    let stood = false;
    const stand = () => {
      stood = true;
      bodies.forEach((b, i) => { const [hx, hy] = homes[i] ?? [0, 0]; b.setVisible(true).setCrop().setPosition(hx, hy).setScale(n); });
      sp.marker.setAlpha(1);
    };
    // a new board or a skip kills the rise's tween without its onComplete or onStop: the arm
    // stands (and sways) by the scene clock regardless
    scene.time.delayedCall(delayMs + EMERGE.churnMs + EMERGE.riseMs + 100, () => { if (stood || !sp.active) return; stand(); this.sway(sp); });
    paint(0);
    sp.marker.setAlpha(0);
    const at = { x: sp.x, y: sp.y };
    scene.time.delayedCall(delayMs, () => {
      if (!sp.active) return;
      sounds();
      if (lead) this.host.shake(EMERGE.churnMs + EMERGE.riseMs, 0.004);
      this.churn(at, ground);
      scene.time.delayedCall(EMERGE.churnMs, () => {
        if (!sp.active) return;
        this.burst(at, ground);
        const st = { k: 0 };
        scene.tweens.add({
          targets: st, k: 1, duration: EMERGE.riseMs, ease: 'Back.easeOut',
          onUpdate: () => { if (sp.active) paint(st.k); },
          onComplete: () => {
            if (!sp.active) return;
            stand();
            this.settle(at, ground, sp.sprite.displayHeight);
            this.sway(sp);
          },
          onStop: () => { if (sp.active) stand(); },
        });
      });
    });
  }

  /** The arm stands swaying about its base (none under reduce motion). */
  sway(sp: UnitSprite): void {
    this.remember(sp, armGround(sp.unit) ?? 'water');
    const id = sp.unit.unitId;
    this.sways.get(id)?.stop();
    this.sways.delete(id);
    if (this.quality.still) return;
    const { deg, ms, phase } = swayOf(id);
    const bodies = sp.bodies;
    const st = { t: phase };
    const tw = this.host.scene.tweens.add({
      targets: st, t: phase + 1, duration: ms, repeat: -1,
      onUpdate: () => {
        if (!sp.active || sp.fallen) return;
        const a = Math.sin(st.t * Math.PI * 2) * deg;
        for (const b of bodies) b.setAngle(a);
      },
    });
    this.sways.set(id, tw);
    sp.once(Phaser.GameObjects.Events.DESTROY, () => { if (this.sways.get(id) === tw) { tw.stop(); this.sways.delete(id); } });
  }

  // ---- parts -----------------------------------------------------------------------------------

  private remember(sp: UnitSprite, ground: CreatureGround): void {
    this.last.set(sp.unit.unitId, { at: { x: sp.x, y: sp.y }, key: sp.sprite.texture.key, frame: sp.sprite.frame.name, flip: sp.sprite.flipX, ground });
  }

  private track<T extends Phaser.GameObjects.GameObject>(o: T): T {
    this.live.add(o);
    o.once(Phaser.GameObjects.Events.DESTROY, () => this.live.delete(o));
    // (and on a skipped playback, whose killed tweens would leave it hanging)
    return oneShot(this.host.scene, o);
  }

  private count(n: number): number { return Math.max(1, Math.round(n * Math.max(0.25, this.quality.density))); }

  /** An arm going under: a ghost of it slides down into the water (or the sand) with a swirl. */
  private sink(id: number, was: { at: P; key: string; frame: string; flip: boolean; ground: CreatureGround }): void {
    this.sunk.push(id);
    if (this.sunk.length > 64) this.sunk.shift();
    const pan = this.host.pan?.(id);
    playSfx('tentacle.sink', { gain: 0.6, ...(pan !== undefined ? { pan } : {}) });
    if (this.quality.still) return;
    const scene = this.host.scene;
    const img = this.track(scene.add.image(was.at.x, was.at.y, was.key, was.frame).setOrigin(0.5, 1).setFlipX(was.flip));
    this.host.add(img);
    const rows = img.frame.height, cols = img.frame.width;
    const st = { k: 1 };
    scene.tweens.add({
      targets: st, k: 0, duration: 520, ease: 'Quad.easeIn',
      onUpdate: () => { const f = riseFrame(st.k, rows); img.setCrop(0, 0, cols, Math.max(0, f.shown)); img.setY(was.at.y + f.sunk); },
      onComplete: () => img.destroy(),
    });
    this.ripple(was.at, was.ground, 0.25, 260);
    this.ripple(was.at, was.ground, 0.15, 520);
  }

  /** The water darkens and churns in a ring, bubbles boil (the sand cracks and trickles in). */
  private churn(at: P, ground: CreatureGround): void {
    const scene = this.host.scene;
    const cell = this.host.cell;
    const look = LOOK[ground];
    const y = at.y - cell * 0.18;
    // the dark below coming up
    const dark = this.track(scene.add.ellipse(at.x, y, cell * 0.4, cell * 0.16, look.dark, 0));
    this.host.addGround(dark);
    scene.tweens.add({ targets: dark, width: cell * 1.7, height: cell * 0.7, fillAlpha: 0.85, duration: EMERGE.churnMs, ease: 'Quad.easeOut' });
    scene.tweens.add({ targets: dark, fillAlpha: 0, delay: EMERGE.churnMs + EMERGE.riseMs, duration: 700, onComplete: () => dark.destroy() });
    // a ring of foam drawn in toward where it will break, as the water is sucked down
    const pull = this.track(scene.add.image(at.x, y, TEX.ring).setTint(look.foam[0]!).setAlpha(0));
    pull.setDisplaySize(cell * 2, cell * 0.8);
    this.host.addGround(pull);
    scene.tweens.add({ targets: pull, alpha: { from: 0.2, to: 0.85 }, displayWidth: cell * 0.6, displayHeight: cell * 0.24, duration: EMERGE.churnMs, ease: 'Quad.easeIn', onComplete: () => pull.destroy() });
    // the churn: foam turning round the ring
    const foam = this.count(12);
    for (let i = 0; i < foam; i++) {
      const dot = this.track(scene.add.image(at.x, y, TEX.px).setTint(look.foam[i % look.foam.length]!).setAlpha(0));
      this.host.addGround(dot);
      const a0 = (i / foam) * Math.PI * 2;
      const st = { t: 0 };
      scene.tweens.add({
        targets: st, t: 1, duration: EMERGE.churnMs + 260,
        onUpdate: () => {
          const r = cell * (0.25 + 0.45 * st.t);
          const a = a0 + st.t * Math.PI * 1.6;
          dot.setPosition(at.x + Math.cos(a) * r, y + Math.sin(a) * r * 0.42).setAlpha(Math.sin(st.t * Math.PI) * 0.9);
          dot.setScale(1.5 + st.t);
        },
        onComplete: () => dot.destroy(),
      });
    }
    if (ground === 'water') {
      // bubbles boiling up and popping
      const bubbles = this.count(10);
      for (let i = 0; i < bubbles; i++) {
        const bx = at.x + (((i * 37) % 17) / 17 - 0.5) * cell * 1.1;
        const by = y + (((i * 53) % 11) / 11 - 0.5) * cell * 0.4;
        const b = this.track(scene.add.image(bx, by, TEX.ring).setTint(0xdff2ff).setAlpha(0).setDisplaySize(2, 2));
        this.host.add(b);
        scene.tweens.add({
          targets: b, alpha: 0.85, displayWidth: 3 + (i % 3), displayHeight: 3 + (i % 3), y: by - 3,
          delay: (i * EMERGE.churnMs) / bubbles * 0.85, duration: 260, yoyo: true, hold: 60, onComplete: () => b.destroy(),
        });
      }
      this.ripple(at, ground, 0.3, 0);
      this.ripple(at, ground, 0.3, EMERGE.churnMs * 0.5);
    } else {
      // cracks run out through the sand from where it will break
      const g = this.track(scene.add.graphics());
      this.host.addGround(g);
      const arms = 6;
      const st = { t: 0 };
      scene.tweens.add({
        targets: st, t: 1, duration: EMERGE.churnMs, ease: 'Quad.easeOut',
        onUpdate: () => {
          g.clear();
          g.lineStyle(1, look.crack, 0.85);
          for (let i = 0; i < arms; i++) {
            const a = (i / arms) * Math.PI * 2 + 0.4;
            const r = cell * 0.75 * st.t;
            const kink = a + 0.35 * Math.sin(i * 2.1);
            g.beginPath();
            g.moveTo(at.x, y);
            g.lineTo(at.x + Math.cos(kink) * r * 0.5, y + Math.sin(kink) * r * 0.5 * 0.42);
            g.lineTo(at.x + Math.cos(a) * r, y + Math.sin(a) * r * 0.42);
            g.strokePath();
          }
        },
      });
      scene.tweens.add({ targets: g, alpha: 0, delay: EMERGE.churnMs + EMERGE.riseMs + 300, duration: 600, onComplete: () => g.destroy() });
      this.trickle(at, look.spray, EMERGE.churnMs);
    }
  }

  /** It breaks the surface: a column of spray and droplets thrown out (or a burst of sand). */
  private burst(at: P, ground: CreatureGround): void {
    const scene = this.host.scene;
    const cell = this.host.cell;
    const look = LOOK[ground];
    const y = at.y - cell * 0.15;
    // the column: thin jets of spray shooting up, ragged and see-through, and falling back (the
    // middle ones tallest; the 2 px texture is drawn a pixel wide)
    const streaks = this.count(7);
    for (let i = 0; i < streaks; i++) {
      const u = streaks > 1 ? i / (streaks - 1) - 0.5 : 0;
      const dx = u * cell * 0.75 + (((i * 7) % 3) - 1);
      const s = this.track(scene.add.image(at.x + dx, y, TEX.px).setTint(look.spray[i % look.spray.length]!).setOrigin(0.5, 1).setScale(0.5, 0.5).setAlpha(0.55 + ((i * 5) % 4) * 0.1));
      this.host.add(s);
      // scale is in texture pixels (2): the tallest jet reaches about a square and a half up
      const peak = (cell * (0.55 + 0.75 * (1 - Math.abs(u) * 1.6)) * (0.8 + ((i * 13) % 5) * 0.08)) / 2;
      scene.tweens.add({ targets: s, scaleY: Math.max(2, peak), duration: 200 + ((i * 3) % 4) * 25, ease: 'Quad.easeOut', yoyo: true, hold: 60, onComplete: () => s.destroy() });
      scene.tweens.add({ targets: s, alpha: 0, delay: 260, duration: 240 });
    }
    // a crown of white foam thrown out round its base
    const crown = this.track(scene.add.image(at.x, y + 1, TEX.ring).setTint(0xffffff).setAlpha(0.95));
    crown.setDisplaySize(cell * 0.5, cell * 0.2);
    this.host.add(crown);
    scene.tweens.add({ targets: crown, displayWidth: cell * 1.5, displayHeight: cell * 0.55, alpha: 0, duration: 520, ease: 'Quad.easeOut', onComplete: () => crown.destroy() });
    // droplets on parabolas
    const drops = this.count(22);
    for (let i = 0; i < drops; i++) {
      const d = this.track(scene.add.image(at.x, y - cell * 0.3, ground === 'water' ? TEX.drop : TEX.px).setTint(look.spray[i % look.spray.length]!).setAlpha(1));
      this.host.add(d);
      const ang = -Math.PI / 2 + (((i * 41) % 23) / 23 - 0.5) * 2.2;
      const v = cell * (1.4 + ((i * 29) % 7) / 7 * 1.6);
      const vx = Math.cos(ang) * v, vy = Math.sin(ang) * v;
      const g = cell * 6;
      const st = { t: 0 };
      const life = 0.55 + ((i * 13) % 5) * 0.06;
      scene.tweens.add({
        targets: st, t: life, duration: life * 1000,
        onUpdate: () => d.setPosition(at.x + vx * st.t, y - cell * 0.3 + vy * st.t + 0.5 * g * st.t * st.t).setAlpha(1 - st.t / life * 0.7),
        onComplete: () => d.destroy(),
      });
    }
    this.ripple(at, ground, 0.6, 0);
    this.ripple(at, ground, 0.45, 180);
  }

  /** Spray falls back round it, and sand pours off the risen arm. */
  private settle(at: P, ground: CreatureGround, h: number): void {
    if (ground === 'sand') this.pour(at, h);
    else this.ripple(at, ground, 0.35, 0);
  }

  /** Sand pours off a risen arm down to the ground for a while. */
  private pour(at: P, h: number): void {
    const scene = this.host.scene;
    const n = this.count(14);
    for (let i = 0; i < n; i++) {
      const g = this.track(scene.add.image(at.x + (((i * 7) % 9) / 9 - 0.5) * 10, at.y - h * (0.35 + ((i * 11) % 6) / 10), TEX.px).setTint(LOOK.sand.spray[i % 4]!).setAlpha(0));
      this.host.add(g);
      scene.tweens.add({ targets: g, alpha: 1, y: at.y - 1, delay: (i * EMERGE.settleMs) / n, duration: 380, ease: 'Quad.easeIn', onComplete: () => g.destroy() });
    }
  }

  /** Sand trickling in toward where it will break, as the ground gives. */
  private trickle(at: P, colours: number[], ms: number): void {
    const scene = this.host.scene;
    const cell = this.host.cell;
    const n = this.count(10);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const r = cell * 0.7;
      const g = this.track(scene.add.image(at.x + Math.cos(a) * r, at.y - cell * 0.18 + Math.sin(a) * r * 0.42, TEX.px).setTint(colours[i % colours.length]!).setAlpha(0.9));
      this.host.addGround(g);
      scene.tweens.add({ targets: g, x: at.x, y: at.y - cell * 0.18, alpha: 0, delay: (i * ms) / n * 0.6, duration: ms * 0.6, ease: 'Quad.easeIn', onComplete: () => g.destroy() });
    }
  }

  /** A ring rolling out flat over the water (dust over the sand). */
  private ripple(at: P, ground: CreatureGround, alpha: number, delay: number): void {
    const scene = this.host.scene;
    const cell = this.host.cell;
    const r = this.track(scene.add.image(at.x, at.y - cell * 0.15, TEX.ring).setTint(LOOK[ground].foam[0]!).setAlpha(0));
    r.setDisplaySize(cell * 0.4, cell * 0.16);
    this.host.addGround(r);
    scene.tweens.add({ targets: r, alpha: { from: alpha * Math.max(0.5, this.quality.density), to: 0 }, displayWidth: cell * 2.2, displayHeight: cell * 0.9, delay, duration: 700, ease: 'Quad.easeOut', onComplete: () => r.destroy() });
  }
}
